import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /**
   * Events that count as a user gesture, after which Safari allows play() again. Scrolling is left
   * out on purpose: it fires without a gesture (page load, scrollIntoView) and Safari does not treat
   * it as one either, so it would only use up the retries.
   */
  static #gestureEvents = ['touchstart', 'pointerdown', 'keydown'];

  /** How many gestures to retry on before giving up. */
  static #maxRetries = 5;

  #retries = 0;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
    this.#listenForGesture();
  }

  disconnectedCallback() {
    this.#stopListeningForGesture();
    super.disconnectedCallback();
  }

  /**
   * Safari refuses to autoplay in Low Power Mode, Low Data Mode or on a poor connection and leaves
   * the video paused on its first frame, but it does allow play() after a user gesture. Retry on
   * the first few gestures until the video is playing.
   */
  #retryPlayback = (/** @type {Event} */ event) => {
    const { videoElement } = this.refs;

    // only real gestures unlock playback, and a copy hidden at this breakpoint has nothing to show
    if (!event.isTrusted || this.getClientRects().length === 0) return;

    if (!videoElement.paused || videoElement.ended) {
      this.#stopListeningForGesture();
      return;
    }

    this.#retries += 1;
    if (this.#retries > VideoBackgroundComponent.#maxRetries) {
      this.#stopListeningForGesture();
      return;
    }

    videoElement
      .play()
      .then(() => this.#stopListeningForGesture())
      .catch(() => {});
  };

  #listenForGesture() {
    for (const event of VideoBackgroundComponent.#gestureEvents) {
      window.addEventListener(event, this.#retryPlayback, { passive: true });
    }
  }

  #stopListeningForGesture() {
    for (const event of VideoBackgroundComponent.#gestureEvents) {
      window.removeEventListener(event, this.#retryPlayback);
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
